import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl, Validators, FormGroup } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { Editor } from 'primeng/editor';
import { MessageService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { HttpClientModule } from '@angular/common/http';
import { ToastModule } from 'primeng/toast';
import { FileService } from '../../core/services/file';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

export interface DrawerField {
  name: string;
  label: string;
  type: 'text' | 'file' | 'editor';
  required?: boolean;
  placeholder?: string;
  accept?: string;
  maxFileSize?: number;
  maxLength?: number;
  editorHeight?: string;
  editorStyle?: any;
}

@Component({
  selector: 'app-drawer',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ButtonModule, DrawerModule, InputTextModule, Editor, BadgeModule, HttpClientModule, ToastModule],
  providers: [MessageService],
  templateUrl: './drawer.html',
  styleUrl: './drawer.scss'
})
export class Drawer implements OnChanges, OnDestroy {
  @Input() visible: boolean = false;
  @Input() header: string = '';
  @Input() fields: DrawerField[] = [];
  @Input() submitLabel: string = 'Submit';
  @Input() position: 'left' | 'right' = 'right';
  @Input() initialData: any = null;
  @Input() width: string = '380px';
  
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() submit = new EventEmitter<any>();
  @Output() fileUpload = new EventEmitter<any>();

  formData: { [key: string]: any } = {};
  isLoading: boolean = false;
  submitLoading: boolean = false;
  imagePreview: string | null = null;
  dragOver: boolean = false;
  private blobUrl: string | null = null; // Track blob URL for cleanup

  fileControl = new FormControl<File[]>([], [this.maxFilesLength(1)]);
  acceptedFiles$ = this.fileControl.valueChanges.pipe(
    map((files) => {
      if (!files || files.length === 0) return [];
      
      if (files.length > 1) {
        const lastFile = files[files.length - 1];
        this.fileControl.setValue([lastFile], { emitEvent: false });
        return [lastFile];
      }
      
      return files;
    })
  );

  rejectedFiles: readonly File[] = [];

  constructor(private fileService: FileService, private messageService: MessageService) {
    // Subscribe to accepted files changes
    this.acceptedFiles$.subscribe(files => {
      if (files.length > 0) {
        this.uploadImage(files[0]);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialData'] && this.initialData) {
      this.formData = { ...this.initialData };
      
      // Handle file fields - if they have URLs, set the preview
      for (const field of this.fields) {
        if (field.type === 'file' && this.initialData[field.name]) {
          const fileUrl = this.initialData[field.name];
          if (typeof fileUrl === 'string' && fileUrl.startsWith('http')) {
            // This is a URL, set it as preview (not a blob URL, so no cleanup needed)
            this.imagePreview = fileUrl;
          }
        }
      }
    }
  }

  ngOnDestroy() {
    // Clean up blob URL to prevent memory leaks
    this.revokeBlobUrl();
  }

  onVisibleChange(value: boolean) {
    this.visible = value;
    this.visibleChange.emit(value);
    if (!value) {
      // Reset form when drawer closes
      this.revokeBlobUrl(); // Clean up blob URL
      this.formData = {};
      this.imagePreview = null;
      this.fileControl.reset();
      this.rejectedFiles = [];
      this.dragOver = false;
      this.submitLoading = false;
      // Reset file input
      this.resetFileInput();
    }
  }

  setSubmitLoading(loading: boolean) {
    this.submitLoading = loading;
  }

  resetForm() {
    this.revokeBlobUrl(); // Clean up blob URL
    this.formData = {};
    this.imagePreview = null;
    this.fileControl.reset();
    this.rejectedFiles = [];
    this.dragOver = false;
    this.submitLoading = false;
    this.resetFileInput();
  }

  isFormValid(): boolean {
    if (this.isLoading || this.dragOver || this.submitLoading) return false;
    
    for (const field of this.fields) {
      if (field.required) {
        const value = this.formData[field.name];
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          if (field.type === 'file' && this.imagePreview) {
            continue;
          }
          return false;
        }
      }
    }
    return true;
  }

  onSubmit() {
    this.submitLoading = true;
    this.submit.emit(this.formData);
    
    setTimeout(() => {
      this.submitLoading = false;
    }, 1000);
  }

  onFileSelected(event: any) {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files) as File[];
      
      // Validate file size before setting the value
      for (const file of fileArray) {
        const maxSize = this.getMaxFileSize();
        if (file.size > maxSize) {
          this.messageService.add({
            severity: 'error',
            summary: 'File too large',
            detail: `File size must be less than ${maxSize / 1000000}MB`
          });
          return;
        }
      }
      
      this.fileControl.setValue(fileArray);
    }
  }

  onReject(files: readonly File[]): void {
    this.rejectedFiles = Array.from(new Set(this.rejectedFiles.concat(files)));
  }

  onRemove(file: File): void {
    this.rejectedFiles = this.rejectedFiles.filter((rejected) => rejected !== file);
    const updatedFiles = this.fileControl.value?.filter((current) => current !== file) ?? [];
    this.fileControl.setValue(updatedFiles);
    if (updatedFiles.length === 0) {
      this.imagePreview = null;
    }
  }

  private uploadImage(file: File): void {
    this.generatePreview(file);

    this.isLoading = true;
    this.fileService.postFile(file, "USER_IMAGE_UPLOAD").subscribe({
      next: (res) => {
        console.log('Drawer: Upload successful', res);
        this.isLoading = false;
        const data = res as any;
        
        // Store the uploaded file URL in formData
        const fileField = this.fields.find(field => field.type === 'file');
        if (fileField) {
          this.formData[fileField.name] = data.url;
        }
        
        this.fileService.fileUploadingSubject.next({
          type: "USER_IMAGE_UPLOAD",
          loading: false,
        });

        this.messageService.add({
          severity: 'success',
          summary: 'File uploaded successfully',
          detail: `${file.name} has been uploaded`
        });

        // Emit file upload event
        this.fileUpload.emit({
          file: file,
          url: data.url,
          field: fileField?.name
        });
      },
      error: (error) => {
        console.error('Drawer: Upload error', error);
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Upload failed',
          detail: error.error?.message || `Failed to upload ${file.name}`
        });
      }
    });
  }

  onFileUpload(event: Event | any) {
    console.log('Drawer: Simple file upload triggered', event);
    
    // Handle both PrimeNG FileUpload event and native input change event
    // In production builds, event.target might be minified, so we check multiple properties
    let files: FileList | null = null;
    
    if (event.files) {
      // PrimeNG FileUpload event
      files = event.files;
    } else if (event.target) {
      // Native input change event
      const target = event.target as HTMLInputElement;
      files = target.files;
    } else if (event.currentTarget) {
      // Fallback for currentTarget
      const target = event.currentTarget as HTMLInputElement;
      files = target.files;
    }
    
    if (!files || files.length === 0) {
      console.log('Drawer: No files in event');
      return;
    }

    // Convert FileList to Array
    const fileArray = Array.from(files) as File[];

    // Validate file size before processing
    for (const file of fileArray) {
      const maxSize = this.getMaxFileSize();
      if (file.size > maxSize) {
        this.messageService.add({
          severity: 'error',
          summary: 'File too large',
          detail: `File size must be less than ${maxSize / 1000000}MB`
        });
        // Reset file input even on validation error
        this.resetFileInput();
        return;
      }
    }

    console.log('Drawer: Processing files', fileArray);
    this.uploadFilesDirectly(fileArray).then(() => {
      // Reset file input after upload completes to allow re-uploading the same file
      this.resetFileInput();
    }).catch(() => {
      // Reset even on error
      this.resetFileInput();
    });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  onDrop(event: DragEvent, field: DrawerField): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) {
      return;
    }

    // Validate file type
    const file = files[0];
    const acceptedTypes = field.accept || 'image/*';
    
    if (!this.isFileTypeValid(file, acceptedTypes)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid file type',
        detail: `Please select a valid file type: ${acceptedTypes}`
      });
      return;
    }

    // Validate file size
    if (field.maxFileSize && file.size > field.maxFileSize) {
      this.messageService.add({
        severity: 'error',
        summary: 'File too large',
        detail: `File size must be less than ${field.maxFileSize / 1000000}MB`
      });
      return;
    }

    // Upload the file
    this.uploadFilesDirectly([file]);
  }


  private isFileTypeValid(file: File, acceptedTypes: string): boolean {
    if (acceptedTypes === '*') return true;
    
    const acceptedTypeArray = acceptedTypes.split(',').map(type => type.trim());
    
    return acceptedTypeArray.some(type => {
      if (type.endsWith('/*')) {
        const category = type.split('/')[0];
        return file.type.startsWith(category + '/');
      }
      return file.type === type;
    });
  }

  // Direct file upload method
  async uploadFilesDirectly(files: File[]): Promise<void> {
    console.log('Drawer: uploadFilesDirectly called with', files.length, 'files');
    
    this.isLoading = true;

    try {
      for (const file of files) {
        console.log('Drawer: Uploading file', file.name);
        
        // Use centralized validation
        const validation = this.fileService.validateFile(file, 'USER_IMAGE_UPLOAD');
        if (!validation.isValid) {
          this.messageService.add({
            severity: 'error',
            summary: 'File validation failed',
            detail: validation.errors.join(', ')
          });
          continue;
        }
        
        try {
          const response = await firstValueFrom(this.fileService.postFile(file, 'USER_IMAGE_UPLOAD'));
          console.log('Drawer: File uploaded successfully', response);
          
          // Store the uploaded file URL in formData
          const fileField = this.fields.find(field => field.type === 'file');
          if (fileField) {
            this.formData[fileField.name] = response.url;
          }

          // Generate preview for image files
          if (file.type.startsWith('image/')) {
            this.generatePreview(file);
          }

          this.messageService.add({
            severity: 'success',
            summary: 'File uploaded successfully',
            detail: `${file.name} has been uploaded`
          });

          // Emit file upload event
          this.fileUpload.emit({
            file: file,
            url: response.url,
            field: fileField?.name
          });

        } catch (error: any) {
          console.error('Drawer: Error uploading file:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Upload failed',
            detail: error.error?.message || `Failed to upload ${file.name}`
          });
          throw error; // Re-throw to allow caller to handle
        }
      }
    } finally {
      this.isLoading = false;
    }
  }

  // Remove current file (clears preview and form data)
  removeFile() {
    console.log('Drawer: Removing current file');
    
    // Revoke blob URL before clearing preview
    this.revokeBlobUrl();
    this.imagePreview = null;
    
    // Clear the file input
    this.resetFileInput();
    
    // Clear form data for file field
    const fileField = this.fields.find(field => field.type === 'file');
    if (fileField) {
      this.formData[fileField.name] = '';
      console.log('Drawer: Cleared file field:', fileField.name);
    }
    
    // Reset file control
    this.fileControl.reset();
    
    this.messageService.add({
      severity: 'info',
      summary: 'File removed',
      detail: 'File has been removed'
    });
  }

  // Generate preview for image files
  private generatePreview(file: File): void {
    try {
      // Revoke previous blob URL if exists
      this.revokeBlobUrl();
      
      // Create new blob URL
      this.blobUrl = URL.createObjectURL(file);
      this.imagePreview = this.blobUrl;
    } catch (error) {
      console.error('Drawer: Error creating object URL:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Preview error',
        detail: 'Failed to generate image preview'
      });
    }
  }

  // Revoke blob URL to prevent memory leaks
  private revokeBlobUrl(): void {
    if (this.blobUrl) {
      try {
        URL.revokeObjectURL(this.blobUrl);
        this.blobUrl = null;
      } catch (error) {
        console.error('Drawer: Error revoking blob URL:', error);
      }
    }
  }

  // Reset file input element
  private resetFileInput(): void {
    const fileField = this.fields.find(field => field.type === 'file');
    if (fileField) {
      const fileInput = document.getElementById('file-input-' + fileField.name) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    }
  }

  formatSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFieldValue(fieldName: string): any {
    return this.formData[fieldName] || '';
  }

  setFieldValue(fieldName: string, value: any) {
    this.formData[fieldName] = value;
  }

  getEditorConfig(field: DrawerField) {
    return {
      height: field.editorHeight || '200px',
      style: field.editorStyle || {
        'height': field.editorHeight || '200px',
        'font-size': '14px'
      },
      formats: ['bold', 'italic', 'underline', 'list', 'bullet', 'link', 'header', 'font', 'size'],
      modules: {
        toolbar: {
          container: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}],
            ['link']
          ]
        }
      }
    };
  }

  // Custom validator for max files length
  private maxFilesLength(maxLength: number) {
    return (control: any) => {
      const value = control.value;
      if (value && value.length > maxLength) {
        return {
          maxLength: {
            message: `Error: maximum limit - ${maxLength} file for upload`
          }
        };
      }
      return null;
    };
  }

  // Helper method to get the maximum file size (defaults to 1MB)
  private getMaxFileSize(): number {
    const fileField = this.fields.find(field => field.type === 'file');
    return fileField?.maxFileSize || 1000000; // Default to 1MB (1000000 bytes)
  }
}
